import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock, Plus } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Field,
  Panel,
  Pill,
  Spinner,
  Stat,
} from '../components/ui.js';

/**
 * Code stock for the `digital` vertical (CLAUDE.md §4).
 *
 * The screen answers the three questions a shop with no shelves cannot answer
 * by looking around: how many are left, who paid and has not received theirs,
 * and what the buyer is told when a code arrives.
 *
 * `undelivered` is given the same weight as `available` on purpose. It is the
 * number that means somebody's money is here and their goods are not — the one
 * an ordinary "in stock" figure hides completely.
 */
export function CodesPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [openProductId, setOpenProductId] = useState<string | null>(null);

  const stock = useQuery({
    queryKey: ['digital-stock', shopId],
    queryFn: () => api.digitalStock(shopId!),
    enabled: Boolean(shopId),
  });

  if (stock.isPending) return <Spinner label={t('common.loading')} />;
  if (stock.isError) {
    return <ErrorBox message={(stock.error as Error | undefined)?.message ?? t('errors.internal')} />;
  }

  const items = stock.data?.items ?? [];
  if (items.length === 0) return <Empty title={t('admin.codesEmpty')} />;

  const waiting = items.reduce((sum, item) => sum + item.undelivered, 0);

  return (
    <div className="flex flex-col gap-4">
      {/*
        Surfaced above everything else when it is non-zero. A merchant should
        never have to open a product to discover that someone paid an hour ago
        and is still waiting.
      */}
      {waiting > 0 ? (
        <div
          className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
          style={{ background: 'var(--color-brand-soft)', color: 'var(--color-brand)' }}
        >
          <Clock className="size-4 shrink-0" aria-hidden="true" />
          {waiting} {t('admin.codesUndelivered')}
        </div>
      ) : null}

      {items.map((item) => (
        <Panel
          key={item.productId}
          title={item.name[locale] || item.name.en}
          action={
            item.low ? (
              <Pill tone="warn">
                <AlertTriangle className="size-3" aria-hidden="true" /> {t('admin.codesLow')}
              </Pill>
            ) : (
              <Pill tone="neutral">{item.status}</Pill>
            )
          }
        >
          <div className="grid grid-cols-3 gap-3">
            <Stat
              label={t('admin.codesInStock')}
              value={item.available}
              tone={item.low ? 'warn' : 'brand'}
            />
            <Stat label={t('admin.codesUndelivered')} value={item.undelivered} />
            <Stat label={t('admin.codesSold')} value={item.sold} />
          </div>

          {openProductId === item.productId ? (
            <CodeEditor
              productId={item.productId}
              instructions={item.instructions}
              lowStockThreshold={item.lowStockThreshold}
              onDone={() => {
                setOpenProductId(null);
                void queryClient.invalidateQueries({ queryKey: ['digital-stock', shopId] });
              }}
            />
          ) : (
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => setOpenProductId(item.productId)}
            >
              <Plus /> {t('admin.codesAdd')}
            </Button>
          )}
        </Panel>
      ))}
    </div>
  );
}

function CodeEditor({
  productId,
  instructions,
  lowStockThreshold,
  onDone,
}: {
  productId: string;
  instructions: string | null;
  lowStockThreshold: number;
  onDone(): void;
}) {
  const { shopId, t } = useSession();
  const [raw, setRaw] = useState('');
  const [notes, setNotes] = useState(instructions ?? '');
  const [threshold, setThreshold] = useState(String(lowStockThreshold));
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<number | null>(null);

  /**
   * One code per line, and that is the only format offered.
   *
   * Merchants paste out of supplier emails and spreadsheet columns, which is
   * exactly what a newline-separated box accepts without asking them to think
   * about delimiters. Blanks are dropped here as well as on the server,
   * because a trailing empty line is not an error worth a message.
   */
  const codes = raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const save = useMutation({
    mutationFn: async () => {
      await api.saveDigitalItem(shopId!, {
        productId,
        instructions: notes.trim() || null,
        lowStockThreshold: Number(threshold) || 0,
      });
      if (codes.length > 0) {
        const result = await api.addDigitalCodes(shopId!, productId, codes);
        return result.added;
      }
      return 0;
    },
    onSuccess: (count) => {
      setAdded(count);
      setRaw('');
      onDone();
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <div className="mt-3 flex flex-col gap-3">
      <Field label={t('admin.codesPaste')} hint={`${codes.length}`}>
        <textarea
          className="min-h-32 w-full rounded-lg p-3 font-mono text-xs"
          style={{ border: '1px solid var(--color-line)', background: 'var(--color-surface)' }}
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          placeholder={'XXXX-1111\nXXXX-2222'}
        />
      </Field>

      <Field label={t('admin.codesInstructions')} hint={t('admin.codesInstructionsHint')}>
        <textarea
          className="min-h-20 w-full rounded-lg p-3 text-sm"
          style={{ border: '1px solid var(--color-line)', background: 'var(--color-surface)' }}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </Field>

      <Field label={t('admin.codesLowThreshold')}>
        <input
          className="w-24 rounded-lg p-2 text-sm"
          style={{ border: '1px solid var(--color-line)' }}
          inputMode="numeric"
          value={threshold}
          onChange={(event) => setThreshold(event.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <div className="flex gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>

      {added !== null ? <p className="text-sm">{t('admin.codesAdded', { count: added })}</p> : null}
      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}
